import type { Channel } from "./types.ts";

export const CHANNELS: { key: Channel; label: string; hint: string }[] = [
  { key: "partner", label: "代理店", hint: "No.1 / 代理店窓口 (no-1@)。受付ハブの No.1 ゲート起票もここ" },
  { key: "customer", label: "顧客", hint: "最終顧客 問い合わせ (support@)" },
  { key: "internal", label: "社内", hint: "社内スレッド・受付ハブの社内ログイン起票（入電・手動起票）" },
  { key: "service_campaign", label: "サービスキャンペーン", hint: "EC-700 現場アプリ (/service/) からの起票・後日対応" },
  { key: "device_monitor", label: "機体監視", hint: "C1/C2 自動起票・予兆スキャン（入電管理 Chat とは別スペース）" },
];

/** 手動起票・区分訂正で選べるチャネル（自動起票専用 device_monitor は除外） */
export const MANUAL_CHANNELS = CHANNELS.filter((c) => c.key !== "device_monitor");

// メール取込の自動区分判定が低確度だったチケットに BE が付けるタグ
// （ops/app/routers/support.py の CHANNEL_REVIEW_TAG と同一文字列にすること）
export const CHANNEL_REVIEW_TAG = "要区分確認";

export const CHANNEL_LABEL: Record<Channel, string> = {
  partner: "代理店",
  customer: "顧客",
  internal: "社内",
  service_campaign: "サービスキャンペーン",
  device_monitor: "機体監視",
};

export const STATUS_LABEL: Record<string, string> = {
  new: "新規",
  open: "未対応",
  in_progress: "対応中",
  waiting: "保留",
  resolved: "解決",
  closed: "クローズ",
};

export const STATUS_FLOW = ["new", "in_progress", "waiting", "resolved", "closed"];

export const NOTIFY_STATUSES = new Set(["in_progress", "waiting", "resolved", "closed"]);

export const STATUS_NOTIFY_PREVIEW: Record<string, string> = {
  in_progress: "対応開始のお知らせ：担当者が対応を開始しました。",
  waiting: "確認のお願い：確認のためお時間をいただいております。",
  resolved: "対応完了のお知らせ：対応が完了しました。",
  closed: "受付終了のお知らせ：受付を終了しました。",
};

export const CATEGORY_LABEL: Record<string, string> = {
  usage: "製品の使い方",
  repair: "故障・修理",
  warranty: "保証・購入証明",
  stock: "在庫・納期",
  sales: "営業・新規取引",
  media: "取材・メディア",
  general: "一般",
  technical: "技術",
  billing: "請求",
};

export const CH_LABEL: Record<string, string> = {
  support: "サポート",
  partner_support: "代理店サポート",
  partner: "代理店窓口",
  sales: "営業",
  pr: "広報",
  bd: "事業開発",
};

export const PRIORITY_COLOR: Record<string, string> = {
  urgent: "var(--danger)",
  high: "var(--warning)",
  normal: "var(--text-secondary, #5f635f)",
  low: "var(--text-disabled, #aeb2ae)",
};

export const PRIORITY_LABEL: Record<string, string> = {
  urgent: "緊急",
  high: "高",
  normal: "通常",
  low: "低",
};

export const STATUS_FILTER_OPTIONS = ["new", "in_progress", "waiting", "resolved", "closed"];
export const PRIORITY_FILTER_OPTIONS = ["urgent", "high", "normal", "low"];

// sla_urgency は BE に無いクライアント側ソート（超過幅の大きい順 → 残り時間の少ない順 → 対象外）。
// 取得時は BE に sla_due_at asc を投げ、受け取ってから逼迫順に並べ替える。
export type TicketSortKey = "sla_urgency" | "created_at" | "updated_at" | "priority" | "status" | "sla_due_at";
export type TicketSortOrder = "desc" | "asc";

export const SORT_KEY_OPTIONS: { key: TicketSortKey; label: string }[] = [
  { key: "sla_urgency", label: "SLA逼迫順" },
  { key: "created_at", label: "起票日" },
  { key: "updated_at", label: "更新日" },
  { key: "sla_due_at", label: "期限" },
  { key: "priority", label: "優先度" },
  { key: "status", label: "ステータス" },
];

export function sortOrderLabel(sortKey: TicketSortKey, order: TicketSortOrder): string {
  if (sortKey === "sla_urgency") return order === "asc" ? "逼迫順" : "余裕順";
  if (sortKey === "priority") return order === "desc" ? "高い順" : "低い順";
  if (sortKey === "status") return order === "desc" ? "降順" : "昇順";
  if (sortKey === "sla_due_at") return order === "asc" ? "近い順" : "遠い順";
  return order === "desc" ? "新しい順" : "古い順";
}

/** 入電・手動対応の既定チャネル（device_monitor＝C1/C2 自動起票は除外） */
export const DEFAULT_CHANNEL_FILTER: Channel[] = ["partner", "customer", "internal", "service_campaign"];

/** 未完了のみ（解決・クローズは既定で非表示） */
export const DEFAULT_STATUS_FILTER: string[] = ["new", "in_progress", "waiting"];

/** 既定は SLA 逼迫順（改善 D-2）。返信のたびに更新日が動く「更新日・新しい順」では
 *  対応済みが上に来てトリアージ順序が崩れるため。「更新日・新しい順」は選択肢として残す。 */
export const DEFAULT_SORT_KEY: TicketSortKey = "sla_urgency";
export const DEFAULT_SORT_ORDER: TicketSortOrder = "asc";

export const VISIT_STATUS_LABEL: Record<string, string> = {
  unscheduled: "未定",
  scheduled: "予定",
  done: "完了",
  canceled: "中止",
};
